import { Box, Stack, styled } from '@mui/material'
import { ArrowUpRightIcon, BuildingOfficeIcon, EyeIcon, EyeSlashIcon, FilesIcon, SealCheckIcon } from '@phosphor-icons/react'
import type { CustomerExistingValuation as ExistingValuation } from '@einwert/valuation-api-client'
import type { ExistingAsset } from '@einwert/valuation-api-client'
import {
    assembleAddressFormat,
    Breadcrumbs,
    ContentChip,
    ContentChipWithOptions,
    HeaderObservers,
    joinDefinedStrings,
    LoadingSkeleton,
    ObserversDrawer,
    TinyNavButton,
    tokens,
    User,
} from 'ComponentLibrary'
import LoadingIndicator from 'ComponentLibrary/src/atoms/LoadingIndicator/LoadingIndicator'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { IconButtonWithOptions, Typography } from 'ComponentLibrary/src/piazza'
import { Option } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButtonWithOptions'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getHeaderBarUserLinkCopy, getHeaderObserversCopy, getObserversDrawerCopy } from './headerBarCopy'

type HeaderBarProps = {
    draft?: ExistingValuation
    asset?: ExistingAsset
    draftOptions: ExistingValuation[]
    isApproved?: boolean
    isVisibleToAppraisers?: boolean
    observerUUIDs: UUID[]
    setObserverUUIDs: (observerUUIDs: UUID[]) => void
    addObserver: (observerUUID: UUID) => void
    removeObserver: (index: number) => void
    users: User[]
    currentUser?: User
    isEditingObservers: boolean
    setIsEditingObservers: (value: boolean) => void
    isEditObserversPending?: boolean
    orderAssignedCustomerUsers: number[]
    orderAssignedAppraiserId?: string
    hasMultipleDrafts: boolean
    isValuationPlausibilityPending?: boolean
    hasNewerDraft?: boolean
}

const getLocalizedDraftChipLabel = ({
    title,
    fallbackLabel,
    formatDraftLabel,
}: {
    title?: string
    fallbackLabel: string
    formatDraftLabel: (version: string) => string
}) => {
    const draftTitle = title?.split(' - ')[0]?.trim()

    if (!draftTitle) {
        return fallbackLabel
    }

    const draftTitleMatch = draftTitle.match(/^(Entwurf|Draft)\s*(.*)$/i)

    if (!draftTitleMatch) {
        return draftTitle
    }

    const draftVersion = draftTitleMatch[2]?.trim()
    return draftVersion ? formatDraftLabel(draftVersion) : fallbackLabel
}

export default function HeaderBar({
    asset,
    draft,
    draftOptions,
    isApproved,
    isVisibleToAppraisers,
    users,
    currentUser,
    observerUUIDs,
    setObserverUUIDs,
    addObserver,
    removeObserver,
    isEditingObservers,
    setIsEditingObservers,
    isEditObserversPending,
    orderAssignedCustomerUsers,
    orderAssignedAppraiserId,
    hasMultipleDrafts,
    isValuationPlausibilityPending,
    hasNewerDraft,
}: HeaderBarProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tHeaderBar } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.headerBar',
    })
    const { pathname } = useLocation()
    const navigate = useNavigate()

    const hideAppraisersAsObservers = !isVisibleToAppraisers
    const usersForObserverDisplay = hideAppraisersAsObservers ? users.filter((user) => user.role !== 'appraiser') : users

    const { street, house_number } = asset?.address ?? {}
    const addressLine = assembleAddressFormat({ street, house_number }, { delimiter: ', ' })
    const customerAssetReference = asset?.customer_asset_reference ? `[${asset?.customer_asset_reference}]` : undefined

    const breadCrumbItems = [
        { title: tCommon('allAssets'), link: '/assets', params: getLatestUrlParams('client-asset-filter') },
        { title: tCommon('asset'), link: `/assets/${asset?.uuid}/overview` },
        { title: tCommon('valuation'), link: `/assets/${asset?.uuid}/valuations/${draft?.uuid}` },
        { title: tCommon('plausibility') },
    ]

    const currentUserObserverIndex = observerUUIDs.findIndex((observerUUID) => observerUUID === currentUser?.uuid)
    const isCurrentUserAssignedCustomer = currentUser?.id ? orderAssignedCustomerUsers.includes(currentUser.id) : false
    const isCurrentUserAssignedAppraiser = currentUser?.uuid === orderAssignedAppraiserId
    const isCurrentUserProtectedObserver = isCurrentUserAssignedCustomer || isCurrentUserAssignedAppraiser
    const shouldDisableObserverRemovalOption = isCurrentUserProtectedObserver && currentUserObserverIndex !== -1
    const isCurrentUserPlatformUser = !!currentUser?.is_platform_user
    const userLinkCopy = getHeaderBarUserLinkCopy(tHeaderBar)
    const headerObserversCopy = getHeaderObserversCopy(tHeaderBar)
    const observersDrawerCopy = getObserversDrawerCopy(tHeaderBar)

    const IconButtonOptions: Option[] = [
        { title: tActions('viewOrder'), callback: () => navigate(`/orders/${draft?.order_uuid}/overview`), icon: <FilesIcon /> },
        { title: tActions('viewAsset'), callback: () => navigate(`/assets/${asset?.uuid}/overview`), icon: <BuildingOfficeIcon /> },
        { title: tHeaderBar('options.editObservers'), callback: () => setIsEditingObservers(true), icon: <EyeIcon /> },
    ]

    if (currentUser?.uuid && !hasNewerDraft) {
        if (currentUserObserverIndex !== -1) {
            IconButtonOptions.push({
                icon: <EyeSlashIcon />,
                title: tHeaderBar('options.stopObservingOrder'),
                callback: () => removeObserver(currentUserObserverIndex),
                disabled: shouldDisableObserverRemovalOption,
            })
        } else {
            IconButtonOptions.push({
                icon: <EyeIcon />,
                title: tHeaderBar('options.observeOrder'),
                callback: () => addObserver(currentUser.uuid),
                disabled: isCurrentUserPlatformUser,
            })
        }
    }

    const draftTitle = draft?.is_draft
        ? getLocalizedDraftChipLabel({
              title: draft.title,
              fallbackLabel: tCommon('draft'),
              formatDraftLabel: (version) => tHeaderBar('draftWithVersion', { version }),
          })
        : undefined

    const navigateToNewDraftPage = (newId: string) => {
        const newPath = pathname.replace(`/${draft?.uuid}`, `/${newId}`)
        void navigate(newPath)
    }
    if (isValuationPlausibilityPending) {
        return (
            <StyledHeaderContainer>
                <Stack direction="column" gap={tokens.SizingSpacing075}>
                    <Breadcrumbs items={breadCrumbItems} currentLocation={pathname} />
                    <LoadingSkeleton variant="rounded" height="32px" width="400px" />
                </Stack>
                <LoadingSkeleton variant="rounded" height="32px" width="300px" />
            </StyledHeaderContainer>
        )
    }

    return (
        <StyledHeaderContainer>
            <Stack direction="column" gap={tokens.SizingSpacing075}>
                <Breadcrumbs items={breadCrumbItems} currentLocation={pathname} />

                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="baseline">
                        <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                            {tCommon('plausibility')}
                        </Typography>
                        <Box maxWidth="300px">
                            <TextWithEllipsis
                                tooltipText={joinDefinedStrings([addressLine, customerAssetReference])}
                                typographyProps={{ variant: 'headline2', color: tokens.ColorTextBoldest }}
                            >
                                {addressLine}
                                {customerAssetReference && (
                                    <Box display="inline-block" marginLeft={tokens.SizingSpacing075} sx={{ verticalAlign: 'text-bottom' }}>
                                        <Typography variant="headline3" color={tokens.ColorTextDefault} noWrap>
                                            {customerAssetReference}
                                        </Typography>
                                    </Box>
                                )}
                            </TextWithEllipsis>
                        </Box>
                    </Stack>
                    {hasMultipleDrafts ? (
                        <ContentChipWithOptions
                            color={'orange'}
                            label={draftTitle}
                            options={draftOptions.map((draftOption) => ({
                                title: getLocalizedDraftChipLabel({
                                    title: draftOption.title,
                                    fallbackLabel: tCommon('draft'),
                                    formatDraftLabel: (version) => tHeaderBar('draftWithVersion', { version }),
                                }),
                                callback: () => navigateToNewDraftPage(draftOption.uuid),
                            }))}
                            selectedOption={draftTitle}
                        />
                    ) : (
                        <>
                            <ContentChip size="medium" highContrast color={'orange'} label={draftTitle} />
                        </>
                    )}

                    {isApproved && (
                        <ContentChip
                            size="medium"
                            color="green"
                            label={tCommon('approved')}
                            icon={<SealCheckIcon size={tokens.SizingIconS} weight="fill" color={tokens.ColorGraphSequentialFourDarkest} />}
                        />
                    )}
                </Stack>
            </Stack>

            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                <TextButtonsContainer>
                    <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                        <LoadingIndicator isPending={!!isEditObserversPending} />
                        <HeaderObservers
                            observerUUIDs={observerUUIDs}
                            users={usersForObserverDisplay}
                            currentUser={currentUser}
                            replaceObserverUUIDs={setObserverUUIDs}
                            setIsEditingObservers={setIsEditingObservers}
                            disabled={hasNewerDraft}
                            disableCurrentUserToggle={isCurrentUserProtectedObserver}
                            copy={headerObserversCopy}
                            userLinkCopy={userLinkCopy}
                        />
                    </Stack>
                </TextButtonsContainer>

                <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                    <NavButtonsContainer>
                        <TinyNavButton
                            leftIcon={<BuildingOfficeIcon />}
                            rightIcon={<ArrowUpRightIcon />}
                            label={tActions('viewAsset')}
                            href={`/assets/${asset?.uuid}/overview`}
                        />
                        <TinyNavButton
                            leftIcon={<FilesIcon />}
                            rightIcon={<ArrowUpRightIcon />}
                            label={tActions('viewOrder')}
                            href={`/orders/${draft?.order_uuid}/overview`}
                        />
                    </NavButtonsContainer>
                    <IconButtonWithOptions variant="tertiary" tooltip={tCommon('options')} size="medium" options={IconButtonOptions} />
                </Stack>
            </Stack>
            <ObserversDrawer
                currentUser={currentUser}
                observerUUIDs={observerUUIDs}
                users={users}
                isEditObservers={isEditingObservers}
                setEditObservers={setIsEditingObservers}
                persistObservers={setObserverUUIDs}
                isPersistingObservers={isEditObserversPending}
                emptyObserversMessage={tHeaderBar('emptyObserversMessage')}
                canEditOtherRoles={false}
                addObserver={addObserver}
                removeObserver={removeObserver}
                orderAssignedCustomerUsers={orderAssignedCustomerUsers}
                orderAssignedAppraiserId={orderAssignedAppraiserId}
                disabled={hasNewerDraft}
                withoutAppraisers={hideAppraisersAsObservers}
                copy={observersDrawerCopy}
                userLinkCopy={userLinkCopy}
            />
        </StyledHeaderContainer>
    )
}

const StyledHeaderContainer = styled('div')(() => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${tokens.ColorBorderDefault}`,
    backgroundColor: tokens.ColorSurfaceDefault,
    boxShadow: tokens.Popup.boxShadow,
    padding: `${tokens.SizingSpacing150} ${tokens.SizingSpacing300}`,
}))

const NavButtonsContainer = styled('div')(() => ({
    display: 'flex',
    gap: tokens.SizingSpacing100,
    '@media (max-width: 1919px)': {
        display: 'none',
    },
}))

const TextButtonsContainer = styled('div')(() => ({
    '@media (max-width: 1280px)': {
        display: 'none',
    },
}))
